"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OpenSeaSDK = void 0;
const EventEmitter = require("events");
const seaport_js_1 = require("@opensea/seaport-js");
const constants_1 = require("@opensea/seaport-js/lib/constants");
const ethers_1 = require("ethers");
const api_1 = require("./api/api");
const constants_2 = require("./constants");
const privateListings_1 = require("./orders/privateListings");
const types_1 = require("./orders/types");
const utils_1 = require("./orders/utils");
const contracts_1 = require("./typechain/contracts");
const types_2 = require("./types");
const utils_2 = require("./utils/utils");
/**
 * The OpenSea SDK main class.
 * @category Main Classes
 */
class OpenSeaSDK {
    /**
     * Create a new instance of OpenSeaSDK.
     * @param signerOrProvider Signer or provider to use for transactions. For example:
     * `new ethers.providers.JsonRpcProvider('https://mainnet.infura.io')` or
     * `new ethers.Wallet(privKey, provider)`
     * @param apiConfig configuration options, including `chain`
     * @param logger optional function for logging debug strings. defaults to no logging
     */
    constructor(signerOrProvider, apiConfig = {}, logger) {
        /** Internal cache of decimals for payment tokens to save network requests */
        this._cachedPaymentTokenDecimals = {};
        this.getAmountWithBasisPointsApplied = (amount, basisPoints) => {
            return ((amount * BigInt(basisPoints)) /
                BigInt(constants_2.INVERSE_BASIS_POINT)).toString();
        };
        // API config
        apiConfig.chain ?? (apiConfig.chain = types_2.Chain.Mainnet);
        this.chain = apiConfig.chain;
        this.api = new api_1.OpenSeaAPI(apiConfig);
        this.provider = (signerOrProvider.provider ??
            signerOrProvider);
        this._signerOrProvider = signerOrProvider ?? this.provider;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.seaport_v1_5 = new seaport_js_1.Seaport(this._signerOrProvider, {
            overrides: { defaultConduitKey: constants_1.OPENSEA_CONDUIT_KEY },
        });
        // Emit events
        this._emitter = new EventEmitter();
        // Logger: default to no logging if fn not provided
        this.logger = logger ?? ((arg) => arg);
        // Cache decimals for WETH payment token to skip network request
        try {
            const wethAddress = (0, utils_2.getWETHAddress)(this.chain).toLowerCase();
            this._cachedPaymentTokenDecimals[wethAddress] = 18;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            if (error.message.includes("Unknown WETH address")) {
                // Ignore
            }
            else {
                console.error(error);
            }
        }
    }
    /**
     * Add a listener for events emitted by the SDK.
     * @param event The {@link EventType} to listen to.
     * @param listener A callback that will accept an object with {@link EventData}\
     * @param once Whether the listener should only be called once, or continue listening until removed.
     */
    addListener(event, listener, once = false) {
        if (once) {
            this._emitter.once(event, listener);
        }
        else {
            this._emitter.addListener(event, listener);
        }
    }
    /**
     * Remove an event listener by calling `.removeListener()` on an event and listener.
     * @param event The {@link EventType} to remove a listener for\
     * @param listener The listener to remove
     */
    removeListener(event, listener) {
        this._emitter.removeListener(event, listener);
    }
    /**
     * Remove all event listeners. This should be called when you're unmounting
     * a component that listens to events to make UI updates.
     * @param event Optional EventType to remove listeners for
     */
    removeAllListeners(event) {
        this._emitter.removeAllListeners(event);
    }
    /**
     * Wrap ETH into WETH.
     * W-ETH is needed for making offers.
     * @param options
     * @param options.amountInEth Amount of ether to wrap
     * @param options.accountAddress Address of the user's wallet containing the ether
     */
    async wrapEth({ amountInEth, accountAddress, }) {
        await this._requireAccountIsAvailable(accountAddress);
        const value = (0, ethers_1.parseEther)(ethers_1.FixedNumber.fromValue(amountInEth).toString());
        this._dispatch(types_2.EventType.WrapEth, { accountAddress, amount: value });
        const wethContract = new ethers_1.Contract((0, utils_2.getWETHAddress)(this.chain), ["function deposit() payable"], this._signerOrProvider);
        wethContract.connect(this.provider);
        try {
            const transaction = await wethContract.deposit({ value });
            await this._confirmTransaction(transaction.hash, types_2.EventType.WrapEth, "Wrapping ETH");
        }
        catch (error) {
            console.error(error);
            this._dispatch(types_2.EventType.TransactionDenied, { error, accountAddress });
        }
    }
    /**
     * Unwrap WETH into ETH.
     * Emits the `UnwrapWeth` event when the transaction is prompted.
     * @param options
     * @param options.amountInEth How much WETH to unwrap
     * @param options.accountAddress Address of the user's wallet containing the WETH
     */
    async unwrapWeth({ amountInEth, accountAddress, }) {
        await this._requireAccountIsAvailable(accountAddress);
        const amount = (0, ethers_1.parseEther)(ethers_1.FixedNumber.fromValue(amountInEth).toString());
        this._dispatch(types_2.EventType.UnwrapWeth, { accountAddress, amount });
        const wethContract = new ethers_1.Contract((0, utils_2.getWETHAddress)(this.chain), ["function withdraw(uint wad) public"], this._signerOrProvider);
        wethContract.connect(this.provider);
        try {
            const transaction = await wethContract.withdraw(amount);
            await this._confirmTransaction(transaction.hash, types_2.EventType.UnwrapWeth, "Unwrapping W-ETH");
        }
        catch (error) {
            console.error(error);
            this._dispatch(types_2.EventType.TransactionDenied, { error, accountAddress });
        }
    }
    async getFees({ collection, seller, paymentTokenAddress, startAmount, endAmount, }) {
        const collectionFees = collection.fees;
        const collectionFeesBasisPoints = (0, utils_2.feesToBasisPoints)(collectionFees);
        const sellerBasisPoints = constants_2.INVERSE_BASIS_POINT - collectionFeesBasisPoints;
        const getConsiderationItem = (basisPoints, recipient) => {
            return {
                token: paymentTokenAddress,
                amount: this.getAmountWithBasisPointsApplied(startAmount, basisPoints),
                endAmount: this.getAmountWithBasisPointsApplied(endAmount ?? startAmount, basisPoints),
                recipient,
            };
        };
        const considerationItems = [];
        if (seller) {
            considerationItems.push(getConsiderationItem(sellerBasisPoints, seller));
        }
        for (const fee of collectionFees) {
            considerationItems.push(getConsiderationItem(fee.fee * 100, fee.recipient));
        }
        return considerationItems;
    }
    getNFTItems(nfts, quantities = []) {
        return nfts.map((nft, index) => ({
            itemType: (0, utils_2.getAssetItemType)(nft.token_standard.toUpperCase()),
            token: (0, utils_2.getAddressAfterRemappingSharedStorefrontAddressToLazyMintAdapterAddress)(nft.contract),
            identifier: nft.identifier ?? undefined,
            amount: quantities[index].toString() ?? "1",
        }));
    }
    /**
     * Create and submit an offer on an asset.
     * @param options
     * @param options.asset The asset to trade. tokenAddress and tokenId must be defined.
     * @param options.accountAddress Address of the wallet making the offer.
     * @param options.startAmount Value of the offer in units, not base units e.g. not wei, of the payment token (or WETH if no payment token address specified)
     * @param options.quantity The number of assets to bid for (if fungible or semi-fungible). Defaults to 1.
     * @param options.domain An optional domain to be hashed and included in the first four bytes of the random salt.
     * @param options.salt Arbitrary salt. If not passed in, a random salt will be generated with the first four bytes being the domain hash or empty.
     * @param options.expirationTime Expiration time for the order, in UTC seconds
     * @param options.paymentTokenAddress ERC20 address for the payment token in the order. If unspecified, defaults to WETH
     * @returns The {@link OrderV2} that was created.
     *
     * @throws Error if the asset does not contain a token id.
     * @throws Error if the accountAddress is not available through wallet or provider.
     * @throws Error if the startAmount is not greater than 0.
     * @throws Error if paymentTokenAddress is not WETH on anything other than Ethereum mainnet.
     */
    async createOffer({ asset, accountAddress, startAmount, quantity = 1, domain, salt, expirationTime, paymentTokenAddress = (0, utils_2.getWETHAddress)(this.chain), }) {
        await this._requireAccountIsAvailable(accountAddress);
        const { nft } = await this.api.getNFT(asset.tokenAddress, asset.tokenId);
        const considerationAssetItems = this.getNFTItems([nft], [BigInt(quantity ?? 1)]);
        const { basePrice } = await this._getPriceParameters(types_2.OrderSide.BID, paymentTokenAddress, expirationTime ?? (0, utils_2.getMaxOrderExpirationTimestamp)(), startAmount);
        const collection = await this.api.getCollection(nft.collection);
        const considerationFeeItems = await this.getFees({
            collection,
            paymentTokenAddress,
            startAmount: basePrice,
        });
        const { executeAllActions } = await this.seaport_v1_5.createOrder({
            offer: [
                {
                    token: paymentTokenAddress,
                    amount: basePrice.toString(),
                },
            ],
            consideration: [...considerationAssetItems, ...considerationFeeItems],
            endTime: expirationTime !== undefined
                ? BigInt(expirationTime).toString()
                : (0, utils_2.getMaxOrderExpirationTimestamp)().toString(),
            zone: constants_2.DEFAULT_ZONE,
            domain,
            salt: BigInt(salt ?? 0).toString(),
            restrictedByZone: false,
            allowPartialFills: true,
        }, accountAddress);
        const order = await executeAllActions();
        return this.api.postOrder(order, {
            protocol: "seaport",
            protocolAddress: utils_1.DEFAULT_SEAPORT_CONTRACT_ADDRESS,
            side: types_2.OrderSide.BID,
        });
    }
    /**
     * Create and submit a listing for an asset.
     * @param options
     * @param options.asset The asset to trade. tokenAddress and tokenId must be defined.
     * @param options.accountAddress  Address of the wallet making the listing
     * @param options.startAmount Value of the listing at the start of the auction in units, not base units e.g. not wei, of the payment token (or WETH if no payment token address specified)
     * @param options.endAmount Value of the listing at the end of the auction. If specified, price will change linearly between startAmount and endAmount as time progresses.
     * @param options.quantity The number of assets to list (if fungible or semi-fungible). Defaults to 1.
     * @param options.domain An optional domain to be hashed and included in the first four bytes of the random salt. This can be used for on-chain order attribution to assist with analytics.
     * @param options.salt Arbitrary salt. If not passed in, a random salt will be generated with the first four bytes being the domain hash or empty.
     * @param options.listingTime Optional time when the order will become fulfillable, in UTC seconds. Undefined means it will start now.
     * @param options.expirationTime Expiration time for the order, in UTC seconds.
     * @param options.paymentTokenAddress ERC20 address for the payment token in the order. If unspecified, defaults to ETH
     * @param options.buyerAddress Optional address that's allowed to purchase this item. If specified, no other address will be able to take the order, unless its value is the null address.
     * @param options.englishAuction If true, the order will be listed as an English auction.
     * @returns The {@link OrderV2} that was created.
     *
     * @throws Error if the asset does not contain a token id.
     * @throws Error if the accountAddress is not available through wallet or provider.
     * @throws Error if the startAmount is not greater than 0.
     * @throws Error if paymentTokenAddress is not WETH on anything other than Ethereum mainnet.
     */
    async createListing({ asset, accountAddress, startAmount, endAmount, quantity = 1, domain, salt, listingTime, expirationTime, paymentTokenAddress = ethers_1.ethers.ZeroAddress, buyerAddress, englishAuction, }) {
        await this._requireAccountIsAvailable(accountAddress);
        const { nft } = await this.api.getNFT(asset.tokenAddress, asset.tokenId);
        const offerAssetItems = this.getNFTItems([nft], [BigInt(quantity ?? 1)]);
        if (englishAuction && paymentTokenAddress == ethers_1.ethers.ZeroAddress) {
            throw new Error(`English auctions must use wrapped ETH or an ERC-20 token.`);
        }
        const { basePrice, endPrice } = await this._getPriceParameters(types_2.OrderSide.ASK, paymentTokenAddress, expirationTime ?? (0, utils_2.getMaxOrderExpirationTimestamp)(), startAmount, endAmount ?? undefined);
        const collection = await this.api.getCollection(nft.collection);
        const considerationFeeItems = await this.getFees({
            collection,
            seller: accountAddress,
            paymentTokenAddress,
            startAmount: basePrice,
            endAmount: endPrice,
        });
        if (buyerAddress) {
            considerationFeeItems.push(...(0, privateListings_1.getPrivateListingConsiderations)(offerAssetItems, buyerAddress));
        }
        const { executeAllActions } = await this.seaport_v1_5.createOrder({
            offer: offerAssetItems,
            consideration: considerationFeeItems,
            startTime: listingTime?.toString(),
            endTime: expirationTime?.toString() ??
                (0, utils_2.getMaxOrderExpirationTimestamp)().toString(),
            zone: englishAuction
                ? (0, utils_2.isTestChain)(this.chain)
                    ? constants_2.ENGLISH_AUCTION_ZONE_TESTNETS
                    : constants_2.ENGLISH_AUCTION_ZONE_MAINNETS
                : constants_2.DEFAULT_ZONE,
            domain,
            salt: BigInt(salt ?? 0).toString(),
            restrictedByZone: englishAuction ? true : false,
            allowPartialFills: englishAuction ? false : true,
        }, accountAddress);
        const order = await executeAllActions();
        return this.api.postOrder(order, {
            protocol: "seaport",
            protocolAddress: utils_1.DEFAULT_SEAPORT_CONTRACT_ADDRESS,
            side: types_2.OrderSide.ASK,
        });
    }
    /**
     * Create and submit a collection offer.
     * @param options
     * @param options.collectionSlug Identifier for the collection.
     * @param options.accountAddress Address of the wallet making the offer.
     * @param options.amount Value of the offer in units, not base units e.g. not wei, of the payment token (or WETH if no payment token address specified).
     * @param options.quantity The number of assets to bid for (if fungible or semi-fungible).
     * @param options.domain An optional domain to be hashed and included in the first four bytes of the random salt. This can be used for on-chain order attribution to assist with analytics.
     * @param options.salt Arbitrary salt. If not passed in, a random salt will be generated with the first four bytes being the domain hash or empty.
     * @param options.expirationTime Expiration time for the order, in UTC seconds.
     * @param options.paymentTokenAddress ERC20 address for the payment token in the order. If unspecified, defaults to WETH.
     * @returns The {@link CollectionOffer} that was created.
     */
    async createCollectionOffer({ collectionSlug, accountAddress, amount, quantity, domain, salt, expirationTime, paymentTokenAddress, }) {
        await this._requireAccountIsAvailable(accountAddress);
        const collection = await this.api.getCollection(collectionSlug);
        const buildOfferResult = await this.api.buildOffer(accountAddress, quantity, collectionSlug);
        const item = buildOfferResult.partialParameters.consideration[0];
        const convertedConsiderationItem = {
            itemType: item.itemType,
            token: item.token,
            identifier: item.identifierOrCriteria,
            amount: item.startAmount,
        };
        const { basePrice } = await this._getPriceParameters(types_2.OrderSide.ASK, paymentTokenAddress, expirationTime ?? (0, utils_2.getMaxOrderExpirationTimestamp)(), amount);
        const considerationFeeItems = await this.getFees({
            collection,
            paymentTokenAddress,
            startAmount: basePrice,
            endAmount: basePrice,
        });
        const considerationItems = [
            convertedConsiderationItem,
            ...considerationFeeItems,
        ];
        const payload = {
            offerer: accountAddress,
            offer: [
                {
                    token: paymentTokenAddress,
                    amount: basePrice.toString(),
                },
            ],
            consideration: considerationItems,
            endTime: expirationTime?.toString() ??
                (0, utils_2.getMaxOrderExpirationTimestamp)().toString(),
            zone: buildOfferResult.partialParameters.zone,
            domain,
            salt: BigInt(salt ?? 0).toString(),
            restrictedByZone: true,
            allowPartialFills: true,
        };
        const { executeAllActions } = await this.seaport_v1_5.createOrder(payload, accountAddress);
        const order = await executeAllActions();
        return this.api.postCollectionOffer(order, collectionSlug);
    }
    /**
     * Fulfill a private order for a designated address.
     * @param options
     * @param options.order The order to fulfill
     * @param options.accountAddress Address of the wallet taking the order.
     * @param options.domain An optional domain to be hashed and included at the end of fulfillment calldata.
     *                       This can be used for on-chain order attribution to assist with analytics.
     * @param options.overrides Transaction overrides, ignored if not set.
     * @returns Transaction hash of the order.
     */
    async fulfillPrivateOrder({ order, accountAddress, domain, overrides, }) {
        if (!order.taker?.address) {
            throw new Error("Order is not a private listing - must have a taker address");
        }
        const counterOrder = (0, privateListings_1.constructPrivateListingCounterOrder)(order.protocolData, order.taker.address);
        const fulfillments = (0, privateListings_1.getPrivateListingFulfillments)(order.protocolData);
        const transaction = await this.seaport_v1_5
            .matchOrders({
            orders: [order.protocolData, counterOrder],
            fulfillments,
            overrides: {
                ...overrides,
                value: counterOrder.parameters.offer[0].startAmount,
            },
            accountAddress,
            domain,
        })
            .transact();
        const transactionReceipt = await transaction.wait();
        if (!transactionReceipt) {
            throw new Error("Missing transaction receipt");
        }
        await this._confirmTransaction(transactionReceipt.hash, types_2.EventType.MatchOrders, "Fulfilling order");
        return transactionReceipt.hash;
    }
    /**
     * Fulfill an order for an asset. The order can be either a listing or an offer.
     * @param options
     * @param options.order The order to fulfill, a.k.a. "take"
     * @param options.accountAddress Address of the wallet taking the offer.
     * @param options.recipientAddress The optional address to receive the order's item(s) or currencies. If not specified, defaults to accountAddress.
     * @param options.domain An optional domain to be hashed and included at the end of fulfillment calldata.  This can be used for on-chain order attribution to assist with analytics.
     * @param options.overrides Transaction overrides, ignored if not set.
     * @returns Transaction hash of the order.
     *
     * @throws Error if the accountAddress is not available through wallet or provider.
     * @throws Error if the order's protocol address is not supported by OpenSea. See {@link isValidProtocol}.
     * @throws Error if attempting to fulfill the order with a recipient address which does not match a private listing.
     */
    async fulfillOrder({ order, accountAddress, recipientAddress, domain, overrides, }) {
        await this._requireAccountIsAvailable(accountAddress);
        const protocolAddress = order.protocolAddress ?? order.protocol_address;
        (0, utils_2.requireValidProtocol)(protocolAddress);
        const orderHash = order.orderHash ?? order.order_hash;
        const side = order.side ??
            ([types_1.OrderType.BASIC, types_1.OrderType.ENGLISH].includes(order.type)
                ? types_2.OrderSide.ASK
                : types_2.OrderSide.BID);
        let extraData = undefined;
        const protocolData = order.protocolData ?? order.protocol_data;
        if (orderHash) {
            const result = await this.api.generateFulfillmentData(accountAddress, orderHash, protocolAddress, side);
            // If the order is using offer protection, the extraData
            // must be included with the order to successfully fulfill.
            const inputData = result.fulfillment_data.transaction.input_data;
            if ("orders" in inputData && "extraData" in inputData.orders[0]) {
                extraData = inputData.orders[0].extraData;
            }
            const signature = result.fulfillment_data.orders[0].signature;
            protocolData.signature = signature;
        }
        const isPrivateListing = "taker" in order ? !!order.taker : false;
        if (isPrivateListing) {
            if (recipientAddress) {
                throw new Error("Private listings cannot be fulfilled with a recipient address");
            }
            return this.fulfillPrivateOrder({
                order: order,
                accountAddress,
                domain,
                overrides,
            });
        }
        const { executeAllActions } = await this.seaport_v1_5.fulfillOrder({
            order: protocolData,
            accountAddress,
            recipientAddress,
            extraData,
            domain,
            overrides,
        });
        const transaction = await executeAllActions();
        const transactionHash = ethers_1.ethers.Transaction.from(transaction).hash;
        if (!transactionHash) {
            throw new Error("Missing transaction hash");
        }
        await this._confirmTransaction(transactionHash, types_2.EventType.MatchOrders, "Fulfilling order");
        return transactionHash;
    }
    /**
     * Cancel orders onchain, preventing them from being fulfilled.
     * @param options
     * @param options.orders The orders to cancel
     * @param options.accountAddress The account address cancelling the orders.
     * @param options.domain An optional domain to be hashed and included at the end of fulfillment calldata.
     *                       This can be used for on-chain order attribution to assist with analytics.
     * @param options.overrides Transaction overrides, ignored if not set.
     * @returns Transaction hash of the order.
     */
    async cancelSeaportOrders({ orders, accountAddress, domain, protocolAddress = utils_1.DEFAULT_SEAPORT_CONTRACT_ADDRESS, overrides, }) {
        const checksummedProtocolAddress = ethers_1.ethers.getAddress(protocolAddress);
        if (checksummedProtocolAddress !== utils_1.DEFAULT_SEAPORT_CONTRACT_ADDRESS) {
            throw new Error(`Only ${utils_1.DEFAULT_SEAPORT_CONTRACT_ADDRESS} is currently supported for cancelling orders.`);
        }
        const transaction = await this.seaport_v1_5
            .cancelOrders(orders, accountAddress, domain, overrides)
            .transact();
        return transaction.hash;
    }
    /**
     * Cancel an order on-chain, preventing it from ever being fulfilled.
     * @param options
     * @param options.order The order to cancel
     * @param options.accountAddress The account address that will be cancelling the order.
     * @param options.domain An optional domain to be hashed and included at the end of fulfillment calldata.  This can be used for on-chain order attribution to assist with analytics.
     *
     * @throws Error if the accountAddress is not available through wallet or provider.
     * @throws Error if the order's protocol address is not supported by OpenSea. See {@link isValidProtocol}.
     */
    async cancelOrder({ order, accountAddress, domain, }) {
        await this._requireAccountIsAvailable(accountAddress);
        (0, utils_2.requireValidProtocol)(order.protocolAddress);
        this._dispatch(types_2.EventType.CancelOrder, { orderV2: order, accountAddress });
        // Transact and get the transaction hash
        const transactionHash = await this.cancelSeaportOrders({
            orders: [order.protocolData.parameters],
            accountAddress,
            domain,
            protocolAddress: order.protocolAddress,
        });
        // Await transaction confirmation
        await this._confirmTransaction(transactionHash, types_2.EventType.CancelOrder, "Cancelling order");
    }
    /**
     * Returns whether an order is fulfillable.
     * An order may not be fulfillable if a target item's transfer function
     * is locked for some reason, e.g. an item is being rented within a game
     * or trading has been locked for an item type.
     * @param options
     * @param options.order Order to check
     * @param options.accountAddress The account address that will be fulfilling the order
     * @returns True if the order is fulfillable, else False.
     *
     * @throws Error if the order's protocol address is not supported by OpenSea. See {@link isValidProtocol}.
     */
    async isOrderFulfillable({ order, accountAddress, }) {
        (0, utils_2.requireValidProtocol)(order.protocolAddress);
        try {
            const isValid = await this.seaport_v1_5
                .validate([order.protocolData], accountAddress)
                .staticCall();
            return !!isValid;
        }
        catch (error) {
            if ((0, utils_2.hasErrorCode)(error) && error.code === "CALL_EXCEPTION") {
                return false;
            }
            throw error;
        }
    }
    /**
     * Get an account's balance of any Asset. This asset can be an ERC20, ERC1155, or ERC721.
     * @param options
     * @param options.accountAddress Account address to check
     * @param options.asset The Asset to check balance for. tokenStandard must be set.
     * @returns The balance of the asset for the account.
     *
     * @throws Error if the token standard does not support balanceOf.
     */
    async getBalance({ accountAddress, asset, }) {
        switch (asset.tokenStandard) {
            case types_2.TokenStandard.ERC20: {
                const contract = new ethers_1.ethers.Contract(asset.tokenAddress, contracts_1.ERC20__factory.createInterface(), this.provider);
                return await contract.balanceOf.staticCall(accountAddress);
            }
            case types_2.TokenStandard.ERC1155: {
                const contract = new ethers_1.ethers.Contract(asset.tokenAddress, contracts_1.ERC1155__factory.createInterface(), this.provider);
                return await contract.balanceOf.staticCall(accountAddress, asset.tokenId);
            }
            case types_2.TokenStandard.ERC721: {
                const contract = new ethers_1.ethers.Contract(asset.tokenAddress, contracts_1.ERC721__factory.createInterface(), this.provider);
                try {
                    const owner = await contract.ownerOf.staticCall(asset.tokenId);
                    return BigInt(owner.toLowerCase() == accountAddress.toLowerCase());
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                }
                catch (error) {
                    this.logger(`Failed to get ownerOf ERC721: ${error.message ?? error}`);
                    return 0n;
                }
            }
            default:
                throw new Error("Unsupported token standard for getBalance");
        }
    }
    /**
     * Instead of signing an off-chain order, this methods allows you to approve an order
     * with on on-chain transaction.
     * @param order Order to approve
     * @param domain An optional domain to be hashed and included at the end of fulfillment calldata.  This can be used for on-chain order attribution to assist with analytics.
     * @returns Transaction hash of the approval transaction
     *
     * @throws Error if the accountAddress is not available through wallet or provider.
     * @throws Error if the order's protocol address is not supported by OpenSea. See {@link isValidProtocol}.
     */
    async approveOrder(order, domain) {
        await this._requireAccountIsAvailable(order.maker.address);
        (0, utils_2.requireValidProtocol)(order.protocolAddress);
        this._dispatch(types_2.EventType.ApproveOrder, {
            orderV2: order,
            accountAddress: order.maker.address,
        });
        const transaction = await this.seaport_v1_5
            .validate([order.protocolData], order.maker.address, domain)
            .transact();
        await this._confirmTransaction(transaction.hash, types_2.EventType.ApproveOrder, "Approving order");
        return transaction.hash;
    }
    /**
     * Compute the `basePrice` and `endPrice` parameters to be used to price an order.
     * Also validates the expiration time and auction type.
     * @param tokenAddress Address of the ERC-20 token to use for trading. Use the null address for ETH.
     * @param expirationTime When the auction expires, or 0 if never.
     * @param startAmount The base value for the order, in the token's main units (e.g. ETH instead of wei)
     * @param endAmount The end value for the order, in the token's main units (e.g. ETH instead of wei)
     */
    async _getPriceParameters(orderSide, tokenAddress, expirationTime, startAmount, endAmount) {
        tokenAddress = tokenAddress.toLowerCase();
        const isEther = tokenAddress === ethers_1.ethers.ZeroAddress;
        let decimals = 18;
        if (!isEther) {
            if (tokenAddress in this._cachedPaymentTokenDecimals) {
                decimals = this._cachedPaymentTokenDecimals[tokenAddress];
            }
            else {
                const paymentToken = await this.api.getPaymentToken(tokenAddress);
                this._cachedPaymentTokenDecimals[tokenAddress] = paymentToken.decimals;
                decimals = paymentToken.decimals;
            }
        }
        const startAmountWei = ethers_1.ethers.parseUnits(startAmount.toString(), decimals);
        const endAmountWei = endAmount
            ? ethers_1.ethers.parseUnits(endAmount.toString(), decimals)
            : undefined;
        const priceDiffWei = endAmountWei !== undefined ? startAmountWei - endAmountWei : 0n;
        const basePrice = startAmountWei;
        const endPrice = endAmountWei;
        // Validation
        if (startAmount == null || startAmountWei < 0) {
            throw new Error(`Starting price must be a number >= 0`);
        }
        if (isEther && orderSide === types_2.OrderSide.BID) {
            throw new Error(`Offers must use wrapped ETH or an ERC-20 token.`);
        }
        if (priceDiffWei < 0) {
            throw new Error("End price must be less than or equal to the start price.");
        }
        if (priceDiffWei > 0 && BigInt(expirationTime) === 0n) {
            throw new Error("Expiration time must be set if order will change in price.");
        }
        return { basePrice, endPrice };
    }
    _dispatch(event, data) {
        this._emitter.emit(event, data);
    }
    /**
     * Throws an error if an account is not available through the provider.
     * @param accountAddress The account address to check is available.
     */
    async _requireAccountIsAvailable(accountAddress) {
        const accountAddressChecksummed = ethers_1.ethers.getAddress(accountAddress);
        const availableAccounts = [];
        if ("address" in this._signerOrProvider) {
            availableAccounts.push(this._signerOrProvider.address);
        }
        else if ("listAccounts" in this._signerOrProvider) {
            const addresses = (await this._signerOrProvider.listAccounts()).map((acct) => acct.address);
            availableAccounts.push(...addresses);
        }
        if (availableAccounts.includes(accountAddressChecksummed)) {
            return;
        }
        throw new Error(`Specified accountAddress is not available through wallet or provider: ${accountAddressChecksummed}. Accounts available: ${availableAccounts.length > 0 ? availableAccounts.join(", ") : "none"}`);
    }
    /**
     * Wait for a transaction to confirm and log the success or failure.
     * @param transactionHash The transaction hash to wait for.
     * @param event The event type to log.
     * @param description The description of the transaction.
     */
    async _confirmTransaction(transactionHash, event, description) {
        const transactionEventData = { transactionHash, event };
        this.logger(`Transaction started: ${description}`);
        try {
            this._dispatch(types_2.EventType.TransactionCreated, transactionEventData);
            await this.provider.waitForTransaction(transactionHash);
            this.logger(`Transaction succeeded: ${description}`);
            this._dispatch(types_2.EventType.TransactionConfirmed, transactionEventData);
        }
        catch (error) {
            this.logger(`Transaction failed: ${description}`);
            this._dispatch(types_2.EventType.TransactionFailed, {
                ...transactionEventData,
                error,
            });
            throw error;
        }
    }
}
exports.OpenSeaSDK = OpenSeaSDK;
//# sourceMappingURL=sdk.js.map